/*
 * Copyright (c) 2016-present Invertase Limited & Contributors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this library except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 */
import { Unsubscribe as AuthUnsubscribe } from "firebase/auth";
import { useEffect } from "react";
import {
  type QueryClient,
  QueryFunction,
  QueryKey,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import { captureException } from "@utils/captureException";

type Unsubscribe = AuthUnsubscribe;

type Subscriptions = {
  unsubscribes: Record<string, Unsubscribe>;
  observerCount: Record<string, number>;
  eventCount: Record<string, number>;
};
const subscriptions = new WeakMap<QueryClient, Subscriptions>();

function subscriptionsFor(queryClient: QueryClient) {
  let current = subscriptions.get(queryClient);
  if (!current) {
    current = { unsubscribes: {}, observerCount: {}, eventCount: {} };
    subscriptions.set(queryClient, current);
  }
  return current;
}

function storeUnsubscribe(
  state: Subscriptions,
  key: string,
  stop: Unsubscribe,
) {
  state.unsubscribes[key] = stop;
}

function incrementEventCount(state: Subscriptions, key: string) {
  state.eventCount[key] ??= 0;
  state.eventCount[key]++;
}

function retainSubscription(state: Subscriptions, key: string) {
  state.observerCount[key] ??= 1;
  state.observerCount[key] += 1;
  return () => {
    state.observerCount[key] -= 1;
    if (state.observerCount[key] !== 1) return;
    state.unsubscribes[key]();
    delete state.unsubscribes[key];
    delete state.eventCount[key];
  };
}

interface CancellablePromise<T = void> extends Promise<T> {
  cancel?: () => void;
}

type UseSubscriptionOptions<TData, TError, R> = UseQueryOptions<
  TData,
  TError,
  R
> & {
  onlyOnce?: boolean;
  fetchFn?: () => Promise<TData>;
};

/**
 * Utility hook to subscribe to events, given a function that returns an observer callback.
 * @param queryKey The react-query queryKey
 * @param subscriptionKey A hashable key to store the subscription
 * @param subscribeFn Returns an unsubscribe function to the event
 * @param options
 * @returns
 */
export function useSubscription<TData, TError, R = TData>(
  queryKey: QueryKey,
  subscribeFn: (cb: (data: TData | null) => Promise<void>) => Unsubscribe,
  options?: UseSubscriptionOptions<TData, TError, R>,
): UseQueryResult<R, TError> {
  const hashFn = options?.queryKeyHashFn || JSON.stringify;
  const subscriptionHash = hashFn(queryKey);
  const queryClient = useQueryClient();
  const state = subscriptionsFor(queryClient);
  const { unsubscribes } = state;

  useEffect(() => {
    if (!options?.onlyOnce) {
      return retainSubscription(state, subscriptionHash);
    }
  }, []);

  const promiseHandlers: {
    resolve: (data: TData | null) => void;
    reject: (err: any) => void;
  } = { resolve: () => null, reject: () => null };
  const result: CancellablePromise<TData | null> = new Promise<TData | null>(
    (resolve, reject) => {
      promiseHandlers.resolve = resolve;
      promiseHandlers.reject = reject;
    },
  );
  const resolvePromise = (data: TData | null) => promiseHandlers.resolve(data);
  const rejectPromise = (err: any) => promiseHandlers.reject(err);

  result.cancel = () => {
    queryClient.invalidateQueries({ queryKey }).catch((error) => {
      captureException(error, {
        channel: "breadcrumb",
        area: "query",
        operation: "invalidate-cancelled-subscription-query",
      });
    });
  };

  let unsubscribe: Unsubscribe;
  if (!options?.onlyOnce) {
    if (unsubscribes[subscriptionHash]) {
      unsubscribe = unsubscribes[subscriptionHash];
      const old = queryClient.getQueryData<TData | null>(queryKey);

      resolvePromise(old || null);
    } else {
      unsubscribe = subscribeFn(async (data) => {
        incrementEventCount(state, subscriptionHash);

        resolvePromise(data || null);
        queryClient.setQueryData(queryKey, data);
      });
      storeUnsubscribe(state, subscriptionHash, unsubscribe);
    }
  } else {
    if (!options.fetchFn) {
      throw new Error("You must specify fetchFn if using onlyOnce mode.");
    } else {
      options
        .fetchFn()
        .then(resolvePromise)
        .catch((err) => {
          rejectPromise(err);
        });
    }
  }

  const queryFn: QueryFunction<TData, QueryKey> = () => {
    return result as Promise<TData>;
  };

  return useQuery<TData, TError, R>({
    ...options,
    queryFn,
    queryKey,
    retry: false,
    staleTime: Infinity,
    refetchInterval: undefined,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}
